import React, { useState, useEffect } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { PageBanner } from '../Banners/PageBanner'
import { useRequests } from '../../requests/useRequests'
import NurserySearch, { NurseryFilters } from './NurserySearch'
import { State } from '../../types/state'
import { useSelector } from 'react-redux'
import { Nursery } from '../../types/nursery'
import AppNavBar from '../Header/AppNavBar'
import ButtonLink from '../Buttons/ButtonLink'

const NurseryList: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams()
  const [filteredNurseries, setFilteredNurseries] = useState<Nursery[]>([])
  const [loading, setLoading] = useState<boolean>(true)
  const [error, setError] = useState<string | null>(null)
  const requests = useRequests()
  const { isSuperadmin } = useSelector((state: State) => state.user)

  // Parametry paginacji
  const [currentPage, setCurrentPage] = useState<number>(1)
  const [itemsPerPage, setItemsPerPage] = useState<number>(12)
  const [totalPages, setTotalPages] = useState<number>(1)
  const [totalCount, setTotalCount] = useState<number>(0)

  // Parsowanie parametrów wyszukiwania z URL
  const getFiltersFromUrl = (): NurseryFilters => {
    const query = searchParams.get('query') || ''
    const tradeTypes = searchParams.getAll('tradeType') || []
    const page = searchParams.get('page') ? parseInt(searchParams.get('page') || '1', 10) : 1
    const limit = searchParams.get('limit') ? parseInt(searchParams.get('limit') || '12', 10) : 12

    // Aktualizowanie stanów paginacji z adresu URL
    if (page !== currentPage) setCurrentPage(page)
    if (limit !== itemsPerPage) setItemsPerPage(limit)

    return {
      searchQuery: query.length > 0 ? query : undefined,
      tradeTypes: tradeTypes.length > 0 ? tradeTypes : undefined,
      page,
      limit
    }
  }

  // Zapisywanie filtrów w URL
  const updateUrlWithFilters = (filters: NurseryFilters) => {
    const newParams = new URLSearchParams()

    if (filters.searchQuery) {
      newParams.set('query', filters.searchQuery)
    }

    if ((filters.tradeTypes != null) && filters.tradeTypes.length > 0) {
      filters.tradeTypes.forEach(type => newParams.append('tradeType', type))
    }

    // Dodanie parametrów paginacji do URL
    if (filters.page) {
      newParams.set('page', filters.page.toString())
    }

    if (filters.limit) {
      newParams.set('limit', filters.limit.toString())
    }

    setSearchParams(newParams)
  }

  // Pobieranie wszystkich szkółek
  useEffect(() => {
    const fetchNurseries = async () => {
      setLoading(true)
      try {
        const filters = getFiltersFromUrl()

        // Zaktualizowane wywołanie API z parametrami paginacji
        const response = await requests.getNurseries({
          page: filters.page || currentPage,
          limit: filters.limit || itemsPerPage,
          query: filters.searchQuery,
          tradeTypes: filters.tradeTypes
        })

        // Pobierz presigned URL dla każdej szkółki
        const nurseriesWithUrls = await Promise.all(response.data.data.map(async (nursery: any) => {
          try {
            const presignedUrlsResponse = await requests.getNurseryPresignedUrls(nursery.id)
            return {
              ...nursery,
              imagePresignedUrl: presignedUrlsResponse.data.imagePresignedUrl || nursery.image
            }
          } catch (error) {
            console.error(`Błąd podczas pobierania presigned URL dla szkółki ${nursery.id}:`, error)
            return {
              ...nursery,
              imagePresignedUrl: nursery.image
            }
          }
        }))

        setFilteredNurseries(nurseriesWithUrls)

        // Aktualizowanie stanu paginacji
        setTotalCount(response.data.meta.total)
        setTotalPages(response.data.meta.pages)
        setError(null)
      } catch (err) {
        console.error('Błąd podczas pobierania szkółek:', err)
        setError('Nie udało się pobrać listy szkółek')
      } finally {
        setLoading(false)
      }
    }

    fetchNurseries()
  }, [requests, currentPage, itemsPerPage, searchParams])

  // Obsługa zmiany strony
  const handlePageChange = (page: number) => {
    setCurrentPage(page)

    const filters = getFiltersFromUrl()
    filters.page = page
    updateUrlWithFilters(filters)

    window.scrollTo(0, 0) // Przewiń do góry przy zmianie strony
  }

  // Obsługa zmiany ilości elementów na stronę
  const handleItemsPerPageChange = (newItemsPerPage: number) => {
    setItemsPerPage(newItemsPerPage)
    setCurrentPage(1)

    const filters = getFiltersFromUrl()
    filters.page = 1
    filters.limit = newItemsPerPage
    updateUrlWithFilters(filters)

    window.scrollTo(0, 0)
  }

  // Obsługa wyszukiwania
  const handleSearch = (filters: NurseryFilters) => {
    // Resetowanie strony przy nowym wyszukiwaniu
    filters.page = 1
    filters.limit = itemsPerPage
    updateUrlWithFilters(filters)
  }

  // Dodanie funkcji mapującej typ handlu na etykiety
  const getTradeTypeLabel = (tradeType: string): string => {
    switch (tradeType) {
      case 'retail':
        return 'Handel detaliczny'
      case 'wholesale':
        return 'Handel hurtowy'
      case 'both':
        return 'Hurtowy i detaliczny'
      default:
        return tradeType
    }
  }

  if (loading) {
    return (
      <div className='text-center pt-100 pb-100'>
        <div className='spinner-border text-primary' role='status'>
          <span className='sr-only'>Ładowanie...</span>
        </div>
      </div>
    )
  }

  return (
    <>
      <AppNavBar
        centerText='Szkółki roślin'
        rightIcons={[]}
      />
      <section className='nurseries-list pt-60 pb-60'>
        <div className='container'>
          <div className='row mb-20'>
            <div className='col-sm-12 tw-flex tw-flex-col md:tw-flex-row md:tw-items-center md:tw-justify-between gap-2'>
              <h2 className='section-title mb-0'>Wszystkie szkółki</h2>
              {isSuperadmin &&
                <ButtonLink
                  to='/nurseries/new'
                  className='tw-mt-3 md:tw-mt-0'
                >
                  <i className='fas fa-plus mr-2' /> Dodaj nową szkółkę
                </ButtonLink>}
            </div>
          </div>

          {/* Dodanie komponentu wyszukiwarki */}
          <NurserySearch
            onSearch={handleSearch}
            initialFilters={getFiltersFromUrl()}
          />

          {error && (
            <div className='alert alert-danger mb-30' role='alert'>
              {error}
            </div>
          )}

          <div className='row'>
            {filteredNurseries.length === 0
              ? (
                <div className='col-12 text-center py-5'>
                  <p>Nie znaleziono żadnych szkółek</p>
                </div>
                )
              : (
                  filteredNurseries.map(nursery => (
                    <div key={nursery.id} className='col-sm-6 col-md-4 col-lg-3 mb-30'>
                      <div className='nursery-card'>
                        <div className='nursery-card__img'>
                          <img
                            src={nursery.imagePresignedUrl || nursery.image || '/placeholder-image.jpg'}
                            className='img-fluid'
                            alt={nursery.name}
                            loading='lazy'
                            onError={(e) => {
                              // Obsługa błędu ładowania obrazu
                              const target = e.target as HTMLImageElement
                              target.onerror = null // Zapobiega pętli błędów
                              target.src = '/placeholder-image.jpg' // Obraz zastępczy
                            }}
                          />
                        </div>
                        <div className='nursery-card__body'>
                          <h5 className='nursery-card__title'>{nursery.name}</h5>
                          <p className='nursery-card__location'><i className='icon-map-marker mr-1' /> {nursery.location}</p>
                          <p className='nursery-card__desc'>{nursery.description.substring(0, 100)}...</p>

                          {nursery.tradeType && (
                            <p className='nursery-card__trade-type small text-muted mb-2'>
                              <i className='icon-store mr-1' /> {getTradeTypeLabel(nursery.tradeType)}
                            </p>
                          )}

                          {nursery.specializations && nursery.specializations.length > 0 && (
                            <div className='nursery-card__tags mb-20'>
                              {nursery.specializations.slice(0, 3).map((spec, index) => (
                                <span key={index} className='badge badge-secondary mr-2 mb-2'>{spec}</span>
                              ))}
                              {nursery.specializations.length > 3 && (
                                <span className='badge badge-light'>+{nursery.specializations.length - 3}</span>
                              )}
                            </div>
                          )}

                          <div className='tw-flex tw-items-center tw-justify-between'>
                            <ButtonLink
                              to={`/nurseries/${nursery.id}`}

                            >
                              <span>Szczegóły</span>
                              <i className='tw-ml-1.5 icon-arrow-right' />
                            </ButtonLink>
                            {isSuperadmin && <Link
                              to={`/nurseries/edit/${nursery.id}`}
                              className='tw-text-blue-500 hover:tw-text-blue-700 focus:tw-text-blue-700'
                                             >
                              <i className='fas fa-edit tw-text-lg' />
                            </Link>}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))
                )}
          </div>

          {/* Paginacja - bezwarunkowo */}
          <div className='pagination-section mt-50 mb-30'>
            <div className='row align-items-center'>
              <div className='col-12'>
                <div className='pagination-info-wrapper text-center mb-4'>
                  <div className='pagination-current-page'>
                    <span className='badge badge-pill badge-primary px-4 py-2 current-page-large'>
                      <span className='page-indicator'>Strona</span> <strong className='page-number'>{currentPage}</strong> <span className='page-indicator'>z</span> <strong className='page-number'>{totalPages}</strong>
                    </span>
                  </div>
                  <div className='pagination-total-info mt-2'>
                    Wyświetlanie <strong>{filteredNurseries.length > 0 ? (currentPage - 1) * itemsPerPage + 1 : 0}</strong> - <strong>{Math.min(currentPage * itemsPerPage, totalCount)}</strong> z <strong>{totalCount}</strong> szkółek
                  </div>
                </div>

                {totalPages > 1 && (
                  <nav aria-label='Nawigacja po szkółkach' className='product-pagination'>
                    <ul className='pagination pagination-lg justify-content-center'>
                      <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                        <a
                          className='page-link'
                          href='#'
                          onClick={(e) => {
                            e.preventDefault()
                            if (currentPage > 1) handlePageChange(currentPage - 1)
                          }}
                          aria-label='Poprzednia'
                        >
                          <span aria-hidden='true'>&laquo;</span>
                          <span className='sr-only'>Poprzednia</span>
                        </a>
                      </li>

                      {Array.from({ length: totalPages }, (_, i) => i + 1)
                        .filter(page =>
                          page === 1 ||
                          page === totalPages ||
                          (page >= currentPage - 2 && page <= currentPage + 2)
                        )
                        .map((page, index, array) => {
                          const showEllipsisBefore = index > 0 && array[index - 1] !== page - 1
                          const showEllipsisAfter = index < array.length - 1 && array[index + 1] !== page + 1

                          return (
                            <React.Fragment key={page}>
                              {showEllipsisBefore && (
                                <li className='page-item disabled'>
                                  <span className='page-link'>...</span>
                                </li>
                              )}

                              <li className={`page-item ${page === currentPage ? 'active' : ''}`}>
                                <a
                                  className='page-link'
                                  href='#'
                                  onClick={(e) => {
                                    e.preventDefault()
                                    handlePageChange(page)
                                  }}
                                >
                                  {page}
                                </a>
                              </li>

                              {showEllipsisAfter && (
                                <li className='page-item disabled'>
                                  <span className='page-link'>...</span>
                                </li>
                              )}
                            </React.Fragment>
                          )
                        })}

                      <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                        <a
                          className='page-link'
                          href='#'
                          onClick={(e) => {
                            e.preventDefault()
                            if (currentPage < totalPages) handlePageChange(currentPage + 1)
                          }}
                          aria-label='Następna'
                        >
                          <span aria-hidden='true'>&raquo;</span>
                          <span className='sr-only'>Następna</span>
                        </a>
                      </li>
                    </ul>
                  </nav>
                )}

                <div className='items-per-page-selector-wrapper text-center mt-4'>
                  <div className='items-per-page-selector d-inline-block'>
                    <label className='mr-2'>Szkółek na stronie:</label>
                    <select
                      className='form-control form-control-sm d-inline-block w-auto'
                      value={itemsPerPage}
                      onChange={(e) => {
                        const newItemsPerPage = parseInt(e.target.value, 10)
                        handleItemsPerPageChange(newItemsPerPage)
                      }}
                    >
                      <option value='12'>12</option>
                      <option value='24'>24</option>
                      <option value='48'>48</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  )
}

export default NurseryList
